import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { apiPost, ApiError } from "@/lib/apiClient";
import { tryCreate, tryDelete, tryGet, tryList, tryUpdate } from "./_shared";

export type StatusWorkflow = Tables<"status_workflows">;
export type StatusWorkflowInsert = Omit<TablesInsert<"status_workflows">, "company_id">;
export type StatusWorkflowUpdate = TablesUpdate<"status_workflows">;

export interface StatusWorkflowWithRelations extends StatusWorkflow {
  from_status?: { id: string; name: string; entity_type: string } | null;
  to_status?: { id: string; name: string } | null;
}

export const statusWorkflowService = {
  get(id: string) {
    return tryGet<StatusWorkflow>(`/api/status-workflows/${encodeURIComponent(id)}`);
  },
  getWithRelations(id: string) {
    return tryGet<StatusWorkflowWithRelations>(
      `/api/status-workflows/${encodeURIComponent(id)}?withRelations=true`,
    );
  },
  list(_companyId: string) {
    return tryList<StatusWorkflow>("/api/status-workflows");
  },
  listWithRelations(_companyId: string) {
    return tryList<StatusWorkflowWithRelations>("/api/status-workflows?withRelations=true");
  },
  listForStatusIds(_companyId: string, statusIds: string[]) {
    if (statusIds.length === 0) {
      return Promise.resolve({ data: [], error: null });
    }
    return tryList<StatusWorkflow & {
      from_status?: { id: string; name: string } | null;
      to_status?: { id: string; name: string } | null;
      created_by_profile?: { full_name: string | null } | null;
      updated_by_profile?: { full_name: string | null } | null;
    }>(`/api/status-workflows?statusIds=${encodeURIComponent(statusIds.join(","))}`);
  },
  listFromStatus(fromStatusId: string) {
    return tryList<StatusWorkflowWithRelations>(
      `/api/status-workflows?fromStatusId=${encodeURIComponent(fromStatusId)}`,
    );
  },
  async getNextSeqNum(_companyId: string): Promise<number> {
    const result = await tryGet<number>("/api/status-workflows/next-seq-num");
    return result.data ?? 1;
  },
  create(data: StatusWorkflowInsert, _context: ServiceContext) {
    return tryCreate<StatusWorkflow>("/api/status-workflows", data);
  },
  update(id: string, data: StatusWorkflowUpdate, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryUpdate<StatusWorkflow>(`/api/status-workflows/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/status-workflows/${encodeURIComponent(id)}`);
  },
  async deleteByStatus(statusId: string, _context: ServiceContext) {
    try {
      const result = await apiPost<{ success: boolean; count: number }>(
        "/api/status-workflows/delete-by-status",
        { status_id: statusId },
      );
      return { success: result.success, error: null, changesLogged: result.count > 0 };
    } catch (err) {
      const e = err instanceof ApiError ? err : err instanceof Error ? err : new Error(String(err));
      return { success: false, error: e, changesLogged: false };
    }
  },
};
